import { fetchAuthSession } from "aws-amplify/auth";
import mockJobs from "../data/mockJobs";

const API_URL = process.env.REACT_APP_API_URL || "";
const USE_MOCK = !API_URL;

// ---------- helpers ----------

async function getAuthToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || "";
  } catch {
    return "";
  }
}

async function request(path, options = {}) {
  const token = await getAuthToken();
  const res = await fetch(`${API_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: token }),
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ---------- in-memory mock state ----------

let _mockJobs = [...mockJobs];

function mockDelay(ms = 300) {
  return new Promise((r) => setTimeout(r, ms));
}

// ---------- Jobs ----------

export async function fetchJobs() {
  if (USE_MOCK) {
    await mockDelay();
    return [..._mockJobs];
  }
  return request("/jobs");
}

export async function updateJob(jobId, updates) {
  if (USE_MOCK) {
    await mockDelay();
    _mockJobs = _mockJobs.map((j) =>
      j.jobId === jobId ? { ...j, ...updates } : j
    );
    return _mockJobs.find((j) => j.jobId === jobId);
  }
  return request(`/jobs/${jobId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

export async function deleteJob(jobId) {
  if (USE_MOCK) {
    await mockDelay();
    _mockJobs = _mockJobs.filter((j) => j.jobId !== jobId);
    return { deleted: jobId };
  }
  return request(`/jobs/${jobId}`, { method: "DELETE" });
}

export async function fetchDescription(jobId) {
  if (USE_MOCK) {
    await mockDelay();
    const job = _mockJobs.find((j) => j.jobId === jobId);
    return {
      description: job?.description || "",
      requirements: job?.requirements || "",
    };
  }
  return request(`/jobs?description=${encodeURIComponent(jobId)}`);
}

export async function fetchCoverLetter(jobId) {
  if (USE_MOCK) {
    await mockDelay();
    const job = _mockJobs.find((j) => j.jobId === jobId);
    return { coverLetter: job?.coverLetter || "" };
  }
  return request(`/jobs/${jobId}/coverletter`);
}

// ---------- Stats ----------

export async function fetchStats() {
  if (USE_MOCK) {
    await mockDelay();
    const total = _mockJobs.length;
    const live = _mockJobs.filter(j => j.status === "new");
    const german = live.filter(j => j.langNote === "German required" || j.langNote === "German likely").length;
    const germanPct = live.length > 0 ? Math.round(german / live.length * 100) : 0;
    const applied = _mockJobs.filter(
      (j) => j.status !== "new"
    ).length;
    const interviews = _mockJobs.filter(
      (j) => j.status === "interview"
    ).length;
    return { total, germanPct, applied, interviews };
  }
  return request("/stats");
}

// ---------- Scraper ----------

let _mockScraperStatus = {
  status: "idle",
  lastRun: "2025-03-05T08:00:00Z",
  jobsFound: 12,
};

export async function triggerScraper() {
  if (USE_MOCK) {
    _mockScraperStatus = { status: "running", lastRun: null, jobsFound: null };
    await mockDelay(2000);
    _mockScraperStatus = {
      status: "done",
      lastRun: new Date().toISOString(),
      jobsFound: Math.floor(Math.random() * 10) + 3,
    };
    return _mockScraperStatus;
  }
  return request("/scrape", { method: "POST" });
}

export async function getScraperStatus() {
  if (USE_MOCK) {
    await mockDelay(100);
    return { ..._mockScraperStatus };
  }
  return request("/scraper/status");
}
